/**
 * ConfidenceBand — renders an evidence-weighted confidence band around a maturity
 * score. The score never moves; the band width reflects how much of the score is
 * backed by documented/verified evidence. Public self-assessments are entirely
 * self-declared, so they honestly show a wide band and 0% verified backing — the
 * hook for the evidence-verified full diagnostic.
 */
import React from "react";
import { ShieldCheck } from "lucide-react";

const TONE = {
  High: { bar: "#16803c", chip: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  Medium: { bar: "#b45309", chip: "bg-amber-50 text-amber-700 border-amber-200" },
  Low: { bar: "#b91c1c", chip: "bg-red-50 text-red-700 border-red-200" },
};

export default function ConfidenceBand({ confidence, sensitivity, className = "", selfDeclared = true }) {
  if (!confidence) return null;
  const { score, scale_min, scale_max, band, verified_pct, confidence_label } = confidence;
  const rng = (scale_max - scale_min) || 1;
  const pct = (v) => (100 * (v - scale_min)) / rng;
  const tone = TONE[confidence_label] || TONE.Low;
  const vpct = Math.round((verified_pct || 0) * 100);

  return (
    <div className={`bg-white border border-zinc-200 p-5 ${className}`} data-testid="confidence-band">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] font-medium tracking-[0.16em] text-zinc-500 uppercase">
          Evidence-weighted confidence
        </div>
        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${tone.chip}`} data-testid="confidence-label">
          {confidence_label} confidence
        </span>
      </div>

      <div className="relative h-3 rounded-full bg-zinc-100 my-3" data-testid="confidence-track">
        <div
          className="absolute top-0 bottom-0 rounded-full"
          style={{ left: `${pct(band.low)}%`, width: `${Math.max(2, pct(band.high) - pct(band.low))}%`, backgroundColor: tone.bar, opacity: 0.3 }}
        />
        <div
          className="absolute -top-1 -bottom-1 w-[3px] rounded"
          style={{ left: `calc(${pct(score)}% - 1.5px)`, backgroundColor: tone.bar }}
        />
      </div>

      <div className="text-[13px] text-zinc-700 leading-relaxed">
        Score <strong className="text-black">{score}/{scale_max}</strong> · evidence-consistent range{" "}
        <strong className="text-black" data-testid="confidence-range">{band.low}–{band.high}</strong> ·{" "}
        <strong className="text-black" data-testid="confidence-verified">{vpct}%</strong> backed by verified evidence
      </div>

      {selfDeclared && (
        <div className="mt-3 flex items-start gap-2 text-[12px] text-zinc-500">
          <ShieldCheck size={14} className="mt-0.5 flex-shrink-0 text-[#7737F6]" />
          <span>
            Only <strong className="text-zinc-700">{vpct}%</strong> of this score is backed by
            independently verified evidence, so the evidence-consistent range is wide
            (<strong className="text-zinc-700">{band.low}–{band.high}</strong>). A full Ekselens
            diagnostic corroborates each answer with evidence — lifting verified backing from{" "}
            <strong className="text-zinc-700">{vpct}%</strong> and tightening this range.
          </span>
        </div>
      )}

      {sensitivity?.has_headroom && (
        <div className="mt-3 border-t border-zinc-100 pt-3 text-[12px] text-zinc-600" data-testid="confidence-sensitivity">
          <div className="text-[11px] font-medium tracking-[0.16em] text-[#7737F6] uppercase mb-1">What tightens this range</div>
          Evidencing{" "}
          <strong className="text-zinc-800">{sensitivity.dimensions.map((d) => d.name).join(", ")}</strong>{" "}
          would narrow the range to{" "}
          <strong className="text-zinc-800">{sensitivity.projected.band.low}–{sensitivity.projected.band.high}</strong>{" "}
          and lift verified backing to{" "}
          <strong className="text-zinc-800">{Math.round((sensitivity.projected.verified_pct || 0) * 100)}%</strong>. The score stays {score}/{scale_max}.
        </div>
      )}
    </div>
  );
}
